/**
 * Blocks Controller
 * Main orchestrator for all block component systems
 * Coordinates click actions, accordions, and sliders
 */

import BlocksClickActions from "./blocks-click-actions.js";
import BlocksAccordionSystem from "./blocks-accordion-system.js";
import BlocksSliderSystem from "./blocks-slider-system.js";

class BlocksController {
	constructor() {
		this.initialized = false;
		this.systems = {
			clickActions: BlocksClickActions,
			accordion: BlocksAccordionSystem,
			slider: BlocksSliderSystem
		};
	}

	// Initialize all block systems
	init() {
		if (this.initialized) return;

		// Initialize systems in order
		this.systems.clickActions.init();
		this.systems.accordion.init();
		this.systems.slider.init();

		// Setup global event listeners for dynamic content
		this.setupDynamicContentHandlers();

		this.initialized = true;
	}

	// Handle dynamic content loading (Shopify sections, AJAX, etc.)
	setupDynamicContentHandlers() {
		// Shopify section events
		document.addEventListener("shopify:section:load", () => this.reinitSystems());
		document.addEventListener("shopify:section:reorder", () => this.reinitSystems());
		document.addEventListener("shopify:section:select", () => this.reinitSystems());
		document.addEventListener("shopify:section:deselect", () => this.reinitSystems());

		// Generic content change detection
		if (typeof MutationObserver !== "undefined") {
			this.setupMutationObserver();
		}
	}

	// Setup mutation observer for dynamic content
	setupMutationObserver() {
		const observer = new MutationObserver(mutations => {
			let shouldReinit = false;

			mutations.forEach(mutation => {
				if (mutation.type === "childList") {
					mutation.addedNodes.forEach(node => {
						if (node.nodeType === Node.ELEMENT_NODE) {
							// Check if added node contains block components
							if (
								node.matches?.("[data-accordion-item], [data-blocks-slider], [data-click-action-type]") ||
								node.querySelector?.("[data-accordion-item], [data-blocks-slider], [data-click-action-type]")
							) {
								shouldReinit = true;
							}
						}
					});
				}
			});

			if (shouldReinit) {
				// Debounce reinit to avoid excessive calls
				clearTimeout(this.reinitTimeout);
				this.reinitTimeout = setTimeout(() => this.reinitSystems(), 100);
			}
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true
		});
	}

	// Re-initialize all systems
	reinitSystems() {
		Object.values(this.systems).forEach(system => {
			if (typeof system.reinit === "function") {
				system.reinit();
			}
		});
	}

	// Configure accordion group (backward compatibility)
	configureAccordionGroup(groupKey, options) {
		this.systems.accordion.configureGroup(groupKey, options);
	}

	// Get system instances
	getSystem(systemName) {
		return this.systems[systemName];
	}

	// Cleanup method for SPA environments
	destroy() {
		Object.values(this.systems).forEach(system => {
			if (typeof system.destroy === "function") {
				system.destroy();
			}
		});

		clearTimeout(this.reinitTimeout);
		this.initialized = false;
	}
}

// Create and export singleton instance
const blocksController = new BlocksController();

// Auto-initialize when DOM is ready
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", () => blocksController.init());
} else {
	blocksController.init();
}

// Expose globally for backward compatibility
window.BlocksController = blocksController;

// Maintain backward compatibility with existing global objects
window.BlockGroupClickActions = blocksController.getSystem("clickActions");
window.AccordionSystem = blocksController.getSystem("accordion");
window.blockGroupSwiperFunctions = {
	initSwiper: container => {
		const sliderSystem = blocksController.getSystem("slider");
		const containerId = typeof container === "string" ? container.replace("#", "") : container.id;
		const containerElement = document.getElementById(containerId);
		if (containerElement) {
			sliderSystem.initializeSlider(containerElement);
		}
	}
};

export default blocksController;
